import * as PropTypes from 'prop-types';

import {Tag} from 'folder-items/FileConstants';
import {GenericRunOutputSamplesTable} from 'lab-automation/output-samples/RunOutputSamplesTableConstants';
import {GenericDataTable} from 'notebook/data-tables/DataTableConstants';
import {ClientId, FromServerId, NLOId, ToServerId} from 'notebook/ELNConstants';
import {GenericResultsTable} from 'notebook/integrations/results/ResultsTableConstants';
import {NoteLinkedObjectType} from 'notebook/util/NoteUtilConstants';
import {AssayRunSchema, AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';

import {AutomationErrorFileGraphQL} from '../../graphql-fragments/lab-automation/__generated/AutomationErrorFileGraphQL';
import {AutomationInputFileGraphQL} from '../../graphql-fragments/lab-automation/__generated/AutomationInputFileGraphQL';
import {AutomationOutputFileGraphQL} from '../../graphql-fragments/lab-automation/__generated/AutomationOutputFileGraphQL';

export const HIDDEN_FIELDS_SHAPE = PropTypes.arrayOf(PropTypes.string.isRequired);

export interface DisplayConfig {
  hideId: boolean;
  hideCreator: boolean;
  hideCreatedAt: boolean;
  hideReviewed?: boolean;
  hiddenFields: Array<string>; // represented as field names
}
export const DISPLAY_CONFIG_SHAPE = PropTypes.shape({
  hideId: PropTypes.bool.isRequired,
  hideCreator: PropTypes.bool.isRequired,
  hideCreatedAt: PropTypes.bool.isRequired,
  hideReviewed: PropTypes.bool.isRequired,
  hiddenFields: HIDDEN_FIELDS_SHAPE.isRequired, // represented as field names
});

export type DisplayConfigWithoutReviewed = Omit<DisplayConfig, 'hideReviewed'>;

export const DISPLAY_CONFIG_WITHOUT_REVIEWED_SHAPE = PropTypes.shape({
  hideId: PropTypes.bool.isRequired,
  hideCreator: PropTypes.bool.isRequired,
  hideCreatedAt: PropTypes.bool.isRequired,
  hiddenFields: HIDDEN_FIELDS_SHAPE.isRequired, // represented as field names
});

export interface NLADVersionedAutomationFiles {
  automationInputFiles: Array<AutomationInputFileGraphQL> | null;
  automationOutputFiles: Array<AutomationOutputFileGraphQL> | null;
  automationErrorFiles: Array<AutomationErrorFileGraphQL> | null;
}

type GenericNLADVersionedData<TIds> = NLADVersionedAutomationFiles & {
  _internalVersion: 3;
  runSchemaName: string | null;
  fields: Array<SchemaField> | null;
  tags: Array<Tag> | null;
  structuredTables: Array<GenericRunOutputSamplesTable<TIds> | GenericResultsTable<TIds>>;
  connectionId: string | null;
  equipmentId: string | null;
};

export type NLADVersionedData = GenericNLADVersionedData<ClientId>;
export type ToServerNLADVersionedData = GenericNLADVersionedData<ToServerId>;
export type FromServerNLADVersionedData = GenericNLADVersionedData<FromServerId>;

/* A note on versioning:
 * Currently, the data in versionedData is non-null for NLADs whose assayRunSchema has
 * any automationInputFileConfigs or automationOutputFileConfigs.
 * In the future, it will be non-null for all new NLADs, but there is no plan to backfill, so we will always
 * support NLADs with all nulls in versionedData.
 * Note that these data may also be null if the corresponding model is null.
 * That is, versionedData.runSchemaName and versionedData.fields will be null if NLAD.assayRunSchema is null,
 * and versionedData.tags will be null if NLAD.assayRunId is null.
 *
 * versionedData._internalVersion exists for ease of iterating on versioning. If we change the keys present
 * in versionedData, we should also increment versionedData._internalVersion.
 */
export type GenericNoteLinkedAssayData<TIds> = {
  type: NoteLinkedObjectType.NOTE_LINKED_ASSAY_DATA;
  assayRunSchema: AssayRunSchema | null;
  assayRunId: string | null;
  assayResultIds: Array<string>;
  assayResultSchemas: Array<AssaySchema>;
  attachedForReview: boolean;
  displayConfigBySchemaId: {[schemaId: string]: DisplayConfig};
  dataTables: Array<GenericDataTable<TIds>>;
  provenanceUuid: string;
  versionedData: GenericNLADVersionedData<TIds>;
} & NLOId<TIds>;

export type NoteLinkedAssayData = GenericNoteLinkedAssayData<ClientId>;
export type ToServerNoteLinkedAssayData = GenericNoteLinkedAssayData<ToServerId>;
export type FromServerNoteLinkedAssayData = GenericNoteLinkedAssayData<FromServerId>;
